import { AddCircle } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  Dialog,
  Grid,
  IconButton,
  LinearProgress,
  TablePagination,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import { GridRowModel } from "@mui/x-data-grid";
import { Spacer, TextField, Toolbar } from "common";
import I18n from "i18n-js";
import React from "react";
import { useNavigate } from "react-router-dom";
import useDataCard, { DataCardProps } from "./useDataCard";

export default function DataCard(props: DataCardProps) {
  const navigate = useNavigate();
  const {
    openDialog,
    itemId,
    ShowDialog,
    CloseDialog,
    getValue,
    onPageChange,
    onPageSizeChange,
  } = useDataCard(props);
  const {
    canCreate,
    canEdit,
    canDelete,
    dialogFullWidth = false,
    editUrl,
    createTitle,
    headerTitle,
    headerItems,
    columnSettings,
    cardSettings,
    toolbarProps,
    rows,
    columns,
    rowCount,
    page,
    pageSize,
    loading,
    renderModalForm,
    cardActions,
    cardHeaderAction,
    deleteItem,
    load,
    prepareColumns,
    canEditCard,
    canDeleteCard,
    showPagination,
    renderCreateButton,
  } = props;

  const getBody = (row: GridRowModel) => {
    if (cardSettings && cardSettings.body) {
      return cardSettings.body(row);
    }

    const enabledColumns = columns.filter(
      column =>
        !(
          cardSettings?.header?.titleField == column.field ||
          cardSettings?.header?.subTitleField == column.field
        )
    );

    if (!enabledColumns.length) {
      return undefined;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {prepareColumns(enabledColumns).map((column, index) => {
          const value = getValue(row, column.field, column.valueFormatter);
          return (
            <Box
              key={index}
              sx={{ mb: 1.5, display: "flex", flexDirection: "column" }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                }}
              >
                {column.headerName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  color: "text.primary",
                  wordBreak: "break-word",
                }}
              >
                {value || "-"}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <React.Fragment>
      {renderModalForm && (
        <Dialog
          open={openDialog}
          onClose={CloseDialog}
          fullWidth={dialogFullWidth}
        >
          {renderModalForm({
            id: itemId,
            afterSave: () => {
              CloseDialog();
              load();
            },
            onClose: CloseDialog,
            listItem: {},
          })}
        </Dialog>
      )}
      <Toolbar sx={{ mb: 2, mt: -1, gap: 1 }}>
        {headerTitle}
        {canCreate &&
          (renderCreateButton ? (
            renderCreateButton(() => ShowDialog())
          ) : (
            <IconButton
              title={createTitle ?? I18n.t("App.addNew")}
              onClick={() => ShowDialog()}
            >
              <AddCircle />
            </IconButton>
          ))}
        <Spacer right />
        {headerItems?.includes("refresh") && (
          <IconButton onClick={load}>
            <RefreshIcon />
          </IconButton>
        )}
        {headerItems?.includes("search") && (
          <TextField
            fullWidth={false}
            InputProps={{
              startAdornment: <SearchIcon />,
              endAdornment: (
                <IconButton
                  onClick={toolbarProps.clearSearch}
                  style={{
                    visibility: toolbarProps.value ? "visible" : "hidden",
                  }}
                >
                  <ClearIcon />
                </IconButton>
              ),
            }}
            placeholder="Search"
            value={toolbarProps.value}
            onChange={toolbarProps.onChange}
          />
        )}
      </Toolbar>
      <Box>
        <LinearProgress
          style={{ visibility: loading ? "visible" : "hidden" }}
        />
      </Box>
      {rows.length > 0 &&
        (() => {
          // Group rows if groupKey is specified
          if (cardSettings?.groupKey) {
            const groupedRows = rows.reduce(
              (groups: { [key: string]: GridRowModel[] }, row) => {
                const groupValue =
                  row[cardSettings.groupKey!] || "Uncategorized";
                if (!groups[groupValue]) {
                  groups[groupValue] = [];
                }
                groups[groupValue].push(row);
                return groups;
              },
              {}
            );

            return Object.entries(groupedRows).map(
              ([groupValue, groupRows]) => (
                <Box key={groupValue} sx={{ mb: 4 }}>
                  {/* Group Header */}
                  {cardSettings.renderGroupHeader ? (
                    cardSettings.renderGroupHeader(groupValue, groupRows)
                  ) : (
                    <Box
                      sx={{
                        mb: 2,
                        pb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {groupValue}
                      </Typography>
                      <Chip
                        label={`${groupRows.length} items`}
                        size="small"
                        sx={{ mt: 1 }}
                        variant="outlined"
                      />
                    </Box>
                  )}

                  {/* Cards in Group */}
                  <Grid container spacing={3}>
                    {groupRows.map((row, index) => {
                      let body = getBody(row);
                      const header = cardSettings && cardSettings.header;
                      const editUri = editUrl
                        ? editUrl.replace(":id", row.id)
                        : "";

                      return (
                        <Grid
                          key={`${groupValue}-${index}`}
                          item
                          xs={12}
                          sm={6}
                          lg={4}
                          xl={3}
                        >
                          <Card
                            sx={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: 4,
                              },
                            }}
                          >
                            {header && (
                              <CardHeader
                                avatar={
                                  header.icon ? header.icon(row) : undefined
                                }
                                title={
                                  header.titleField
                                    ? getValue(row, header.titleField)
                                    : undefined
                                }
                                subheader={
                                  header.subTitleField
                                    ? getValue(row, header.subTitleField)
                                    : undefined
                                }
                                action={
                                  cardHeaderAction
                                    ? cardHeaderAction({
                                        id: row.id,
                                        row: row,
                                        columns: columns,
                                        getValue: (id, field) => {
                                          let column = columns.find(
                                            item => (item.field = field)
                                          );

                                          return getValue(
                                            row,
                                            field,
                                            column?.valueFormatter
                                          );
                                        },
                                      })
                                    : undefined
                                }
                              />
                            )}
                            {body && (
                              <CardContent sx={{ flexGrow: 1 }}>
                                {body}
                              </CardContent>
                            )}
                            <CardActions
                              sx={{ justifyContent: "flex-end", pt: 0 }}
                            >
                              {canDelete && (
                                <IconButton
                                  title={I18n.t("App.delete")}
                                  color="error"
                                  onClick={() => {
                                    deleteItem(row);
                                  }}
                                  disabled={
                                    canDeleteCard && !canDeleteCard({ row })
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                              <Spacer right />
                              {canEdit && (
                                <IconButton
                                  title={I18n.t("App.edit")}
                                  color="primary"
                                  onClick={() => {
                                    if (renderModalForm) {
                                      ShowDialog(row.id);
                                    } else {
                                      navigate(editUri);
                                    }
                                  }}
                                  disabled={
                                    canEditCard && !canEditCard({ row })
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                              )}
                              {cardActions?.({
                                id: row.id,
                                row: row,
                                columns: columns,
                                getValue: (id, field) => {
                                  let column = columns.find(
                                    item => (item.field = field)
                                  );

                                  return getValue(
                                    row,
                                    field,
                                    column?.valueFormatter
                                  );
                                },
                              }).map((Element, index) => {
                                return React.cloneElement(Element, {
                                  key: index,
                                });
                              })}
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )
            );
          }

          // Default rendering without grouping
          return (
            <Grid container spacing={3}>
              {rows.map((row, index) => {
                let body = getBody(row);
                const header = cardSettings && cardSettings.header;
                const editUri = editUrl ? editUrl.replace(":id", row.id) : "";

                return (
                  <Grid key={index} item xs={12} sm={6} lg={4} xl={3}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      {header && (
                        <CardHeader
                          avatar={header.icon ? header.icon(row) : undefined}
                          title={
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {header.titleField
                                ? getValue(row, header.titleField)
                                : undefined}
                            </Typography>
                          }
                          subheader={
                            <Typography variant="body2" color="text.secondary">
                              {header.subTitleField
                                ? getValue(row, header.subTitleField)
                                : undefined}
                            </Typography>
                          }
                          action={
                            cardHeaderAction
                              ? cardHeaderAction({
                                  id: row.id,
                                  row: row,
                                  columns: columns,
                                  getValue: (id, field) => {
                                    let column = columns.find(
                                      item => (item.field = field)
                                    );

                                    return getValue(
                                      row,
                                      field,
                                      column?.valueFormatter
                                    );
                                  },
                                })
                              : undefined
                          }
                        />
                      )}
                      {body && (
                        <CardContent sx={{ flexGrow: 1 }}>{body}</CardContent>
                      )}
                      <CardActions sx={{ justifyContent: "flex-start", pt: 0 }}>
                        {canDelete && (
                          <IconButton
                            title={I18n.t("App.delete")}
                            color="error"
                            onClick={() => {
                              deleteItem(row);
                            }}
                            disabled={canDeleteCard && !canDeleteCard({ row })}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                        <Spacer right />
                        {canEdit && (
                          <IconButton
                            color="primary"
                            title={I18n.t("App.edit")}
                            onClick={() => {
                              if (renderModalForm) {
                                ShowDialog(row.id);
                              } else {
                                navigate(editUri);
                              }
                            }}
                            disabled={canEditCard && !canEditCard({ row })}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {cardActions?.({
                          id: row.id,
                          row: row,
                          columns: columns,
                          getValue: (id, field) => {
                            let column = columns.find(
                              item => (item.field = field)
                            );

                            return getValue(row, field, column?.valueFormatter);
                          },
                        }).map((Element, index) => {
                          return React.cloneElement(Element, { key: index });
                        })}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}
      {showPagination !== false && (
        <Box justifyContent="flex-end">
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 20, 50, 100]}
            count={rowCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={onPageSizeChange}
          />
        </Box>
      )}
    </React.Fragment>
  );
}
